import axios, { AxiosError, AxiosResponse } from "axios";
import { reportError, reportWarning } from "./helpers/error-helper";
import {
  describeSlowRequest,
  startRequestTimingTracking,
} from "./request-timing";

declare module "axios" {
  export interface AxiosRequestConfig {
    // Retry this non-GET request while the server answers 503
    // DATABASE_UNAVAILABLE. Only for requests that are safe to send twice.
    retryWhileDatabaseUnavailable?: boolean;
    // How many times the retry below has sent this request again; set only by it.
    databaseUnavailableAttempt?: number;
  }
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime: number;
      requestStartedAt: number;
      hiddenAtStart: boolean;
    };
  }
}

// ~30s in total, enough to outlast the ~25s the database was read-only during
// Heroku Postgres maintenance (DORA-485).
const DATABASE_UNAVAILABLE_RETRY_DELAYS_MS = [2000, 4000, 8000, 8000, 8000];

// Long enough for the browser to have reported the request's resource timing
// entry, which can arrive after axios has the response.
const SLOW_REPORT_DELAY_MS = 1000;

const instance = axios.create({
  xsrfCookieName: "mytoken",
  xsrfHeaderName: "csrf-token",
});

// The server's version id at the time this tab loaded index.html; requests
// whose X-App-Version differs from the current /api/version come from a tab
// opened before the latest deploy. Still a placeholder under the Vite dev server.
const appVersion: string | undefined = (window as any).env?.appVersion;
if (appVersion && appVersion !== "__APP_VERSION__") {
  instance.defaults.headers.common["X-App-Version"] = appVersion;
  instance.defaults.headers.common["X-App-Platform"] = "dispatcher-web";
}

if (process.env.NODE_ENV !== "development" && process.env.NODE_ENV !== "test") {
  // Add performance tracking interceptors here
  startRequestTimingTracking();
  instance.interceptors.request.use((config) => {
    config.metadata = {
      startTime: Date.now(),
      requestStartedAt: performance.now(),
      hiddenAtStart: document.hidden,
    };
    return config;
  });
}

instance.interceptors.response.use(
  (response) => {
    // Status 0 means the browser cancelled the request (seen on Firefox when
    // in-flight requests are torn down) and no HTTP response was received.
    // Axios still resolves it, with data "", which then fails the io-ts decode
    // at the root (Sentry DISPATCHER-A5). Reject it as the network error it
    // is, so it takes the existing network-error path instead.
    if (response.status === 0) {
      return Promise.reject(
        new AxiosError(
          "Network Error",
          AxiosError.ERR_NETWORK,
          response.config,
          response.request
        )
      );
    }
    const { metadata, url, method } = response.config;
    if (metadata && url) {
      const duration = Date.now() - metadata.startTime;
      if (duration > 4000 && url !== "/api/log-entries/bulk") {
        const responseHandledAt = performance.now();
        setTimeout(() => {
          reportWarning({
            message: "Slow response",
            args: url,
            actionType: method?.toUpperCase(),
            err: { duration },
            context: describeSlowRequest({
              url,
              requestStartedAt: metadata.requestStartedAt,
              responseHandledAt,
              hiddenAtStart: metadata.hiddenAtStart,
            }),
          });
        }, SLOW_REPORT_DELAY_MS);
      }
    }
    return response;
  }
  // (error) => {
  //   if (error.config?.metadata) {
  //     const duration = Date.now() - error.config.metadata.startTime;
  //     console.log(`${error.config.method?.toUpperCase()} ${error.config.url} (error): ${duration}ms`);
  //   }
  //   return Promise.reject(error);
  // }
);

// The server answers 503 DATABASE_UNAVAILABLE when the database is briefly out
// of reach; nothing was saved, so the request can be sent again. Reads are
// retried automatically, writes only when they opt in, since the user should
// decide whether to repeat a change that did not go through.
instance.interceptors.response.use(undefined, async (error: unknown) => {
  if (!isDatabaseUnavailableResponse(error)) {
    return Promise.reject(error);
  }
  const config = error.config;
  const method = config?.method?.toLowerCase();
  if (
    !config ||
    (method !== "get" &&
      method !== "head" &&
      !config.retryWhileDatabaseUnavailable)
  ) {
    return Promise.reject(error);
  }
  const attempt = config.databaseUnavailableAttempt ?? 0;
  if (attempt >= DATABASE_UNAVAILABLE_RETRY_DELAYS_MS.length) {
    return Promise.reject(error);
  }
  await new Promise((r) =>
    setTimeout(r, DATABASE_UNAVAILABLE_RETRY_DELAYS_MS[attempt])
  );
  return instance.request({
    ...config,
    databaseUnavailableAttempt: attempt + 1,
  });
});

export const isDatabaseUnavailableResponse = (e: unknown): e is AxiosError =>
  axios.isAxiosError(e) &&
  e.response?.status === 503 &&
  (e.response.data as { code?: string } | undefined)?.code ===
    "DATABASE_UNAVAILABLE";

export default instance;
export const isAxiosError = axios.isAxiosError;
export const getData = <T extends AxiosResponse<any, any>>(response: T) =>
  response.data;
